/**
 * @file AttributesTab.js
 * @description Attributes tab component for the Multi-tenant SSO Wizard.
 */

import React, { useState, useEffect } from 'react';
import SSOAttributeValueDropdown from '../../shared/asyncDropdowns/SSOAttributeValueDropdown';
import AttributeTable from '../components/AttributeTable';
import { Dropdown } from '../../helpers/Dropdown';
import style from '../sso.module.scss';
import { getText } from '../../../i18n';

const NAME_ID_FORMAT_OPTIONS = [
    { value: 'transient', label: getText('msg_45d5bc5a6500') },
    { value: 'unspecified', label: getText('msg_a6e7eb706e11') },
];

const getNameIdFormatLabel = value => {
    const selectedOption = NAME_ID_FORMAT_OPTIONS.find(
        option => option.value === value,
    );

    if (selectedOption) return selectedOption.label;

    return NAME_ID_FORMAT_OPTIONS[1].label;
};

const AttributesTab = ({ data, onChange }) => {
    const [formData, setFormData] = useState(data);
    const [isLiteralMode, setIsLiteralMode] = useState(false);

    useEffect(() => {
        if (formData.literal && !formData.nameId) {
            setIsLiteralMode(true);
        }
    }, []);

    useEffect(() => {
        onChange(formData);
    }, [formData, onChange]);

    const handleDropdownChange = (name, value) => {
        if (value === 'literal') {
            setIsLiteralMode(true);
            setFormData(prevData => ({
                ...prevData,
                [name]: null,
                literal: '',
            }));
        } else {
            setIsLiteralMode(false);
            setFormData(prevData => ({
                ...prevData,
                [name]: value,
                literal: null,
            }));
        }
    };

    const handleLiteralChange = value => {
        setFormData(prevData => ({
            ...prevData,
            literal: value,
        }));
    };

    const handleAttributesChange = attributes => {
        setFormData(prevData => ({
            ...prevData,
            ssoAttributes: attributes,
        }));
    };

    const handleNameIdFormatChange = value => {
        setFormData(prevData => ({
            ...prevData,
            nameIdFormat: value,
        }));
    };

    return (
        <div
            className={`col-12 col-xs-12 col-md-12 col-lg-12 m-auto pb-5 px-5  ${style.AttributesTabContainer}`}
        >
            <div className="col-12 col-md-12 col-lg-4 py-4 d-lg-flex">
                <SSOAttributeValueDropdown
                    selectedAttributeValue={
                        isLiteralMode ? 'literal' : formData.nameId
                    }
                    onAttributeValueChange={valueId =>
                        handleDropdownChange('nameId', valueId)
                    }
                    inlineLabel={false}
                    isRequired={true}
                    label={getText('msg_076d71b4bc2c')}
                    addLiteral={true}
                />
                {isLiteralMode && (
                    <div className="form-group col-12 ms-lg-2">
                        <label htmlFor="literal-input">{getText('msg_92e97e035f44')}</label>
                        <input
                            type="text"
                            id="literal-input"
                            className="form-control"
                            value={formData.literal || ''}
                            onChange={e => handleLiteralChange(e.target.value)}
                            placeholder={getText('msg_cc74ae1ccf46')}
                        />
                    </div>
                )}
            </div>

            <div className="form-group mb-3 col-12 col-md-12 col-lg-4 pb-4">
                <label htmlFor="name-id-format">
                    {getText('msg_e5437b2eb57b')} <span className="text-danger">*</span>
                </label>
                <Dropdown
                    id="name-id-format"
                    defaultValue={getNameIdFormatLabel(formData.nameIdFormat)}
                    options={NAME_ID_FORMAT_OPTIONS}
                    value={formData.nameIdFormat || 'unspecified'}
                    onChange={handleNameIdFormatChange}
                />
            </div>

            <AttributeTable
                attributes={formData.ssoAttributes || []}
                onChange={handleAttributesChange}
            />
        </div>
    );
};

export default AttributesTab;
